<template>
  <backbar-page-layout
    back-title="title.user_details"
    :title="title"
    :user-id="id"
    :loading="isLoadingStatus"
  >
    <b-bsq-modal
      ref="modal"
      v-model="isModalEnabled"
      :header-title="modalHeaderTitle"
      :button-title="modalButtonTitle"
      :button-variant="modalButtonVariant"
      @ok="modalSubmit"
    >
      <div class="mt-3 mb-5">
        <div v-if="isAddContactModalEnabled">
          <i18n path="description.add_contact" tag="div" class="mb-3">
            <span place="contact">{{ $t(`general.${type}`).toLowerCase() }}</span>
            <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
          </i18n>
          <b-form-contact-input
            v-if="type === 'phone'"
            required
            v-focus
            v-model="contactValue"
            :label="$t(`general.${type}`)"
          />
          <b-bsq-input
            v-else
            required
            v-focus
            v-model="contactValue"
            :label="$t(`general.${type}`)"
          />
        </div>
        <div v-else-if="isDeleteContactModalEnabled">
          <i18n path="description.delete_contact" tag="div" class="mb-3">
            <span place="contact">{{ $t(`general.${type}`).toLowerCase() }}</span>
          </i18n>
          <div class="font-weight-bold text-center">
            {{ deleteContactValue }}
          </div>
        </div>
        <div v-else-if="isUpdatePrimaryContactModalEnabled">
          <i18n path="description.update_primary_contact" tag="div" class="mb-3">
            <span place="contact">{{ $t(`general.${type}`).toLowerCase() }}</span>
          </i18n>
          <div class="font-weight-bold text-center">
            {{ updatePrimaryContactValue }}
          </div>
        </div>
      </div>
    </b-bsq-modal>
    <template #alert>
      <b-row>
        <b-col>
          <b-alert
            :show="resendDone"
            variant="success"
          >
            {{ $t('status.success.resend_verification') }}
          </b-alert>
          <b-alert
            :show="resendError !== undefined"
            variant="danger"
          >
            {{ $t(resendError) }}
          </b-alert>
        </b-col>
      </b-row>
    </template>
    <template #action>
      <b-button
        variant="outline-primary"
        @click="isAddContactModalEnabled = !isAddContactModalEnabled"
      >
        {{ $t('actions.add_contact', { contact: $t(`general.${type}`) }) }}
      </b-button>
    </template>
    <b-container class="mt-4">
      <b-table
        class="theme-general"
        :items="items"
        :fields="fields"
      >
        <template v-slot:cell(value)="data">
          <span>{{ data.item.value }}</span>
          <span v-if="data.item.isPrimary">{{ $t('general.blank') }}{{ $t('general.blank') }}({{ $t('general.primary') }})</span>
          <span v-if="!data.item.isVerified" class="text-danger">{{ $t('general.blank') }}{{ $t('general.blank') }}({{ $t('verification.non_verified') }})</span>
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown no-caret right variant="link" toggle-class="p-0">
            <template #button-content>
              <i aria-hidden="true" class="fas fa-ellipsis-h" />
            </template>
            <b-dropdown-item-button
              v-if="!data.item.isPrimary"
              @click="startDeleteContact(data.item)"
            >
              {{ $t('actions.delete_contact', { contact: $t(`general.${type}`).toLowerCase() }) }}
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="!data.item.isVerified"
              @click="resendVerification(data.item)"
            >
              {{ $t('actions.resend_verification') }}
            </b-dropdown-item-button>
            <b-dropdown-item-button
              v-if="!data.item.isPrimary && data.item.isVerified === $t('verification.verified')"
              @click="startUpdatePrimaryContact(data.item)"
            >
              {{ $t('actions.update_primary_contact') }}
            </b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </b-container>
  </backbar-page-layout>
</template>

<script>
import { mapState } from 'vuex'

import BackbarPageLayout from '@/components/BackbarPageLayout'

import store from '@/store'
import i18n from '@/i18n'

export default {
  name: 'UserDetailsContact',
  components: {
    BackbarPageLayout
  },

  props: {
    id: {
      type: Number,
      required: true
    },
    type: {
      type: String,
      required: true,
      default: ''
    }
  },

  data () {
    return {
      fields: [
        { key: 'value', label: i18n.t(`general.${this.type}`) },
        { key: 'actions', label: i18n.t('title.actions') }
      ],
      contactValue: '',
      deleteContactId: undefined,
      updatePrimaryContactId: undefined,

      isAddContactModalEnabled: false,
      isDeleteContactModalEnabled: false,
      isUpdatePrimaryContactModalEnabled: false
    }
  },

  computed: {
    ...mapState('client', [
      'authenticated'
    ]),
    ...mapState('management/userDetails', [
      'user'
    ]),
    ...mapState('management/userDetails/contactsList', [
      'loading',

      'resendDone',
      'resendError',
      'actionError'
    ]),
    ...mapState('management/userDetails/contactsList', {
      items: state => state.contacts
    }),
    isLoadingStatus () {
      return !this.authenticated || this.loading
    },
    title () {
      return `general.${this.type}`
    },
    deleteContactValue () {
      let deleteItem = this.items.find(item => item.id === this.deleteContactId)
      if (deleteItem !== undefined) {
        deleteItem = deleteItem.value
      }
      return deleteItem
    },
    updatePrimaryContactValue () {
      return this.items.find(item => item.id === this.updatePrimaryContactId).value
    },
    isModalEnabled: {
      get () {
        return this.isAddContactModalEnabled ||
            this.isDeleteContactModalEnabled ||
            this.isUpdatePrimaryContactModalEnabled
      },
      set () {
        setTimeout(() => {
          this.isAddContactModalEnabled = false
          this.isDeleteContactModalEnabled = false
          this.isUpdatePrimaryContactModalEnabled = false

          // Reset deleteContactId and updatePrimaryContactId when modal is not enabled
          this.deleteContactId = undefined
          this.updatePrimaryContactId = undefined
        }, 500)
      }
    },
    modalHeaderTitle () {
      if (this.isAddContactModalEnabled) {
        return i18n.t('actions.add_contact', { contact: i18n.t(`general.${this.type}`).toLowerCase() })
      } else if (this.isDeleteContactModalEnabled) {
        return i18n.t('actions.delete_contact', { contact: i18n.t(`general.${this.type}`).toLowerCase() })
      } else if (this.isUpdatePrimaryContactModalEnabled) {
        return i18n.t('actions.update_primary_contact')
      }
      return ''
    },
    modalButtonTitle () {
      if (this.isAddContactModalEnabled) {
        return i18n.t('actions.add_contact', { contact: i18n.t(`general.${this.type}`).toLowerCase() })
      } else if (this.isDeleteContactModalEnabled) {
        return i18n.t('actions.delete_contact', { contact: i18n.t(`general.${this.type}`).toLowerCase() })
      } else if (this.isUpdatePrimaryContactModalEnabled) {
        return i18n.t('actions.update_primary_contact')
      }
      return ''
    },
    modalButtonVariant () {
      if (this.isAddContactModalEnabled || this.isUpdatePrimaryContactModalEnabled) {
        return 'primary'
      } else if (this.isDeleteContactModalEnabled) {
        return 'danger'
      }
      return ''
    }
  },

  watch: {
    async authenticated (newVal) {
      if (newVal) {
        await store.dispatch('management/userDetails/contactsList/list', {
          type: this.type
        })
      } else {
        // Clear the error message from unauthenticated state
        store.commit('management/userDetails/contactsList/CLEAR_STATES')
      }
    }
  },

  created () {},
  async mounted () {
    await store.dispatch('management/userDetails/contactsList/list', {
      type: this.type
    })
  },
  updated () {},
  destroyed () {
    store.commit('management/userDetails/contactsList/CLEAR_STATES')
  },

  methods: {
    startDeleteContact (item) {
      this.deleteContactId = item.id
      this.isDeleteContactModalEnabled = !this.isDeleteContactModalEnabled
    },
    startUpdatePrimaryContact (item) {
      this.updatePrimaryContactId = item.id
      this.isUpdatePrimaryContactModalEnabled = !this.isUpdatePrimaryContactModalEnabled
    },
    async modalSubmit () {
      if (this.isAddContactModalEnabled) {
        await this.addContact()
      } else if (this.isDeleteContactModalEnabled) {
        await this.deleteContact()
      } else if (this.isUpdatePrimaryContactModalEnabled) {
        await this.updatePrimaryContact()
      }
      this.$nextTick(() => {
        this.isAddContactModalEnabled = false
        this.isDeleteContactModalEnabled = false
        this.isUpdatePrimaryContactModalEnabled = false
      })
    },
    async addContact () {
      await store.dispatch('management/userDetails/contactsList/add', {
        type: this.type,
        contactValue: this.contactValue
      })
    },
    async deleteContact () {
      await store.dispatch('management/userDetails/contactsList/delete', this.deleteContactId)
    },
    async resendVerification (item) {
      await store.dispatch('management/userDetails/contactsList/resendContactVerification', item.id)
    },
    async updatePrimaryContact () {
      await store.dispatch('management/userDetails/contactsList/updatePrimaryContact', {
        type: this.type,
        contactId: this.updatePrimaryContactId
      })
    }
  }
}
</script>
